import React, { useEffect, useState } from 'react';
import { Input, Modal, Select } from 'antd';
import styles from '@pages/api-portal/styles.module.scss';
import { GROUP_ICON_OPTIONS } from '@pages/api-portal/constants';
import { ApiGroup } from '@pages/api-portal/types';

export interface GroupFormData {
    groupKey: string;
    name: string;
    description: string;
    icon: string;
}

const DEFAULT_VALUES: GroupFormData = { groupKey: '', name: '', description: '', icon: 'workflow' };

interface GroupFormDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: GroupFormData) => Promise<void>;
    isSubmitting: boolean;
    group?: ApiGroup | null;
}

/*
 * Create/edit dialog for API group metadata. In edit mode, groupKey is hidden since it's
 * immutable after creation.
 */
export function GroupFormDialog({ open, onClose, onSubmit, isSubmitting, group }: GroupFormDialogProps) {
    const isEdit = !!group;
    const [form, setForm] = useState<GroupFormData>(DEFAULT_VALUES);

    useEffect(() => {
        if (!open) return;
        if (group) {
            setForm({
                groupKey: group.groupKey ?? String(group.id),
                name: group.name,
                description: group.description,
                icon: group.icon,
            });
        } else {
            setForm(DEFAULT_VALUES);
        }
    }, [open, group]);

    const isValid =
        form.name.trim().length > 0 && (isEdit || /^[a-z0-9-]+$/.test(form.groupKey.trim()));

    return (
        <Modal
            title={isEdit ? 'Edit API Group' : 'Create API Group'}
            visible={open}
            onCancel={onClose}
            onOk={() => onSubmit(form)}
            okButtonProps={{ disabled: !isValid, loading: isSubmitting }}
            okText={isEdit ? 'Save Changes' : 'Create'}
            wrapClassName="apimModal"
        >
            {!isEdit && (
                <div style={{ marginBottom: 12 }}>
                    <label className={styles.fieldLabel}>
                        Group Key <span className={styles.requiredMark}>*</span>
                    </label>
                    <Input
                        placeholder="e.g., authentication"
                        value={form.groupKey}
                        onChange={(e) => setForm({ ...form, groupKey: e.target.value })}
                    />
                    <p className={styles.fieldHint}>Unique identifier for this API group.</p>
                </div>
            )}
            <div style={{ marginBottom: 12 }}>
                <label className={styles.fieldLabel}>
                    Name <span className={styles.requiredMark}>*</span>
                </label>
                <Input
                    placeholder="e.g., Authentication"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
            </div>
            <div style={{ marginBottom: 12 }}>
                <label className={styles.fieldLabel}>Description</label>
                <Input.TextArea
                    placeholder="Describe what this group contains..."
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
            </div>
            <div>
                <label className={styles.fieldLabel}>Icon</label>
                <Select style={{ width: '100%' }} value={form.icon} onChange={(icon: string) => setForm({ ...form, icon })}>
                    {GROUP_ICON_OPTIONS.map((option) => (
                        <Select.Option key={option.value} value={option.value}>
                            {option.label}
                        </Select.Option>
                    ))}
                </Select>
            </div>
        </Modal>
    );
}
